import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bot, Lock, RefreshCw, RotateCcw, Send } from 'lucide-react';
import { useFinancialContext } from '../hooks/useFinancialContext';
import { useUserAccess } from '../hooks/useUserAccess';
import { supabase } from '../lib/supabaseClient';
import {
  AI_PROVIDERS,
  AiProvider,
  aiProviderOptionLabel,
  formatAiProviderName,
  readStoredProvider,
  storeProvider,
  normalizeProvider,
} from '../lib/aiProviders';
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const SUGGESTED_QUESTIONS = [
  'Quel est mon bilan du mois ?',
  'Où puis-je réduire mes dépenses ?',
  'Combien puis-je épargner ce mois-ci ?',
  'Quelles dépenses ont augmenté récemment ?',
];

const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL || '/api/chat-financier';

/** Répare les tableaux Markdown collés sur une seule ligne par l'IA. */
function normalizeAssistantMarkdown(content: string): string {
  return content
    .replace(/\|\s*\|\s*(-[-|\s:]*\|)/g, '|\n|$1')
    .replace(/\|\s*\|\s*([A-Za-zÀ-ÿ0-9(])/g, '|\n| $1');
}

const AnalyseIA: React.FC = () => {
  const { hasFeature, loading: accessLoading, isFree } = useUserAccess();
  const assistantEnabled = hasFeature('assistant_ia');
  const { context, loading: contextLoading, error: contextError, refresh } = useFinancialContext();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [lastProvider, setLastProvider] = useState<string | null>(null);
  const [provider, setProvider] = useState<AiProvider>(() => readStoredProvider());
  const [quotaRemaining, setQuotaRemaining] = useState<number | null>(null);
  const [quotaLimit, setQuotaLimit] = useState<number | null>(null);
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const refreshQuota = useCallback(async () => {
    if (!isFree) {
      setQuotaRemaining(null);
      setQuotaLimit(null);
      setQuotaExhausted(false);
      return;
    }
    const { data, error } = await supabase.rpc('get_ai_chat_quota', { p_limit: 10 });
    if (error || !data) return;
    const payload = data as {
      remaining?: number | null;
      limit?: number | null;
      upgrade_required?: boolean;
      unlimited?: boolean;
    };
    if (payload.unlimited) {
      setQuotaRemaining(null);
      setQuotaLimit(null);
      setQuotaExhausted(false);
      return;
    }
    setQuotaLimit(typeof payload.limit === 'number' ? payload.limit : 10);
    setQuotaRemaining(typeof payload.remaining === 'number' ? payload.remaining : null);
    setQuotaExhausted(!!payload.upgrade_required);
  }, [isFree]);

  useEffect(() => {
    if (assistantEnabled) {
      refreshQuota();
    }
  }, [assistantEnabled, refreshQuota]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, sending, scrollToBottom]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || sending || quotaExhausted) return;

      if (!context) {
        setChatError('Les données financières ne sont pas encore disponibles.');
        return;
      }

      setChatError(null);
      setSending(true);
      const userMessage: ChatMessage = { role: 'user', content: trimmed };
      const historyForApi = messages;
      setMessages((prev) => [...prev, userMessage]);
      setInput('');

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(CHAT_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            message: trimmed,
            history: historyForApi,
            context,
            provider,
          }),
        });
        const data = await response.json().catch(() => ({}));

        if (response.status === 429 || data?.code === 'AI_QUOTA_EXCEEDED' || data?.upgrade_required) {
          const upgradeMessage =
            data?.reply ||
            data?.error ||
            'Vous avez atteint la limite de 10 requêtes par jour avec un compte gratuit. Passez à Premium pour un accès illimité à l\'Assistant IA.';
          setQuotaExhausted(true);
          setQuotaRemaining(0);
          setMessages((prev) => [...prev, { role: 'assistant', content: upgradeMessage }]);
          return;
        }

        if (!response.ok) {
          throw new Error(
            data?.error || 'Impossible de contacter l\'assistant. Vérifiez que le serveur est démarré.',
          );
        }

        setLastProvider(data.provider || null);
        if (data.quota && !data.quota.unlimited) {
          setQuotaLimit(typeof data.quota.limit === 'number' ? data.quota.limit : 10);
          setQuotaRemaining(
            typeof data.quota.remaining === 'number' ? data.quota.remaining : null,
          );
          setQuotaExhausted(
            typeof data.quota.remaining === 'number' ? data.quota.remaining <= 0 : false,
          );
        }
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: data.reply || 'Réponse vide.' },
        ]);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Erreur réseau lors de l\'envoi du message.';
        setChatError(message);
        setMessages((prev) => prev.slice(0, -1));
        setInput(trimmed);
      } finally {
        setSending(false);
      }
    },
    [context, messages, sending, provider, quotaExhausted],
  );
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleNewConversation = () => {
    setMessages([]);
    setChatError(null);
    setLastProvider(null);
  };

  const showSuggestions = messages.length === 0 && !sending;

  if (accessLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-dark-400">
        Chargement…
      </div>
    );
  }

  if (!assistantEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center space-y-3">
        <Lock className="w-12 h-12 text-dark-500" />
        <h2 className="text-xl font-semibold text-white">Assistant IA indisponible</h2>
        <p className="text-dark-400 max-w-md">
          Cette fonctionnalité n&apos;est pas activée pour votre compte.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] max-h-[calc(100vh-4rem)]">
      {/* En-tête */}
      <div className="flex items-center justify-between px-1 pb-4 border-b border-dark-800 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary-500/20 flex items-center justify-center">
            <Bot className="w-5 h-5 text-primary-500" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Assistant financier</h1>
            <p className="text-sm text-dark-400">
              Conseils personnalisés basés sur vos données
              {isFree && quotaLimit != null && quotaRemaining != null && (
                <span className="ml-2 text-primary-400">
                  · {quotaRemaining}/{quotaLimit} aujourd&apos;hui
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <label className="flex items-center gap-2 rounded-lg border border-dark-700 bg-dark-900 px-3 py-1.5 text-sm text-dark-200">
            <span className="text-dark-400">IA</span>
            <select
              className="bg-transparent font-medium text-white outline-none cursor-pointer max-w-[12rem] sm:max-w-none"
              value={provider}
              onChange={(e) => {
                const next = normalizeProvider(e.target.value);
                setProvider(next);
                storeProvider(next);
              }}
              disabled={sending}
            >
              {AI_PROVIDERS.map((p) => (
                <option key={p} value={p} className="bg-dark-900">
                  {aiProviderOptionLabel(p)}
                </option>
              ))}
            </select>
          </label>
          {lastProvider && (
            <span className="text-xs text-dark-400 bg-dark-800 px-2 py-1 rounded-full">
              via {formatAiProviderName(lastProvider)}
            </span>
          )}
          <button            type="button"
            onClick={() => refresh()}
            disabled={contextLoading}
            className="p-2 rounded-md text-dark-400 hover:text-white hover:bg-dark-800 transition-colors disabled:opacity-50"
            title="Actualiser les données"
          >
            <RefreshCw className={`w-4 h-4 ${contextLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={handleNewConversation}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md text-dark-300 hover:text-white hover:bg-dark-800 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Nouvelle conversation
          </button>
        </div>
      </div>

      {/* Alerte contexte */}
      {contextError && (
        <div className="mt-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm shrink-0">
          {contextError}
        </div>
      )}
      {contextLoading && !context && (
        <div className="mt-3 text-sm text-dark-400 shrink-0">Chargement de vos données financières…</div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-4 min-h-0">
        {showSuggestions && (
          <div className="space-y-3">
            <p className="text-sm text-dark-400 text-center">
              Posez une question ou choisissez une suggestion :
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => sendMessage(q)}
                  disabled={contextLoading || !context || sending || quotaExhausted}
                  className="text-left text-sm px-4 py-3 rounded-lg border border-dark-700 bg-dark-900 text-dark-200 hover:border-primary-500/50 hover:bg-dark-800 transition-colors disabled:opacity-50"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div
            key={`${msg.role}-${idx}`}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] sm:max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                msg.role === 'user'
                  ? 'bg-primary-500 text-white rounded-br-md whitespace-pre-wrap'
                  : 'bg-dark-800 text-dark-100 rounded-bl-md border border-dark-700'
              }`}
            >
              {msg.role === 'assistant' ? (
                <div className="chat-assistant-markdown overflow-x-auto">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {normalizeAssistantMarkdown(msg.content)}
                  </ReactMarkdown>
                </div>
              ) : (
                msg.content
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex justify-start">
            <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-dark-800 border border-dark-700">
              <span className="inline-flex gap-1 text-dark-400">
                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
              </span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quota free épuisée */}
      {quotaExhausted && (
        <div className="mb-2 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm shrink-0 flex items-center justify-between gap-3">
          <span>
            Limite quotidienne atteinte (10 requêtes). Passez à Premium pour continuer.
          </span>
          <a
            href="/settings"
            className="shrink-0 rounded-lg bg-white/90 px-3 py-1 text-xs font-bold text-dark-950 hover:bg-white"
          >
            Passer à Premium
          </a>
        </div>
      )}

      {/* Erreur chat */}
      {chatError && (
        <div className="mb-2 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 text-sm shrink-0">
          {chatError}
        </div>
      )}

      {/* Saisie */}
      <form
        onSubmit={handleSubmit}
        className="flex gap-2 pt-3 border-t border-dark-800 shrink-0"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            quotaExhausted
              ? 'Limite quotidienne atteinte — passez à Premium'
              : 'Posez votre question financière…'
          }
          disabled={sending || contextLoading || !context || quotaExhausted}
          className="flex-1 px-4 py-2.5 rounded-lg bg-dark-900 border border-dark-700 text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={sending || !input.trim() || contextLoading || !context || quotaExhausted}
          className="px-4 py-2.5 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Send className="w-4 h-4" />
          <span className="hidden sm:inline">Envoyer</span>
        </button>
      </form>
    </div>
  );
};

export default AnalyseIA;
